/**
 * NFT Image Generator
 * Dynamically generate NFT lottery ticket images
 */

// Base configuration
const CONFIG = {
  width: 400,
  height: 600,
  backgroundColor: '#0a0a0a',
  borderColor: '#333',
  accentColor: '#fff',
  goldColor: '#ffd700',
  silverColor: '#c0c0c0',
  bronzeColor: '#cd7f32'
};

// Prize level colors
const PRIZE_COLORS = {
  0: CONFIG.goldColor,   // Grand Prize
  1: CONFIG.silverColor, // First Prize
  2: CONFIG.bronzeColor, // Second Prize
  3: '#888'              // Third Prize
};

/**
 * Generate NFT image Canvas
 * @param {Object} nftData - NFT data
 * @param {string} nftData.number - 6-digit number
 * @param {string} nftData.signature - Personal signature
 * @param {number} nftData.drawId - Draw period
 * @param {number} nftData.tokenId - Token ID
 * @param {number} nftData.prizeLevel - Prize level (0-3, -1 means no prize)
 * @param {boolean} nftData.isListed - Whether listed for sale
 * @returns {HTMLCanvasElement}
 */
export function generateNFTCanvas(nftData) {
  const canvas = document.createElement('canvas');
  canvas.width = CONFIG.width;
  canvas.height = CONFIG.height;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = CONFIG.backgroundColor;
  ctx.fillRect(0, 0, CONFIG.width, CONFIG.height);

  // Border
  ctx.strokeStyle = CONFIG.borderColor;
  ctx.lineWidth = 2;
  ctx.strokeRect(10, 10, CONFIG.width - 20, CONFIG.height - 20);

  // Inner border decoration
  ctx.strokeStyle = '#222';
  ctx.lineWidth = 1;
  ctx.strokeRect(20, 20, CONFIG.width - 40, CONFIG.height - 40);

  // Top title area
  drawHeader(ctx, nftData);

  // Number display area
  drawNumber(ctx, nftData.number);

  // Prize badge (if any)
  if (nftData.prizeLevel >= 0) {
    drawPrizeBadge(ctx, nftData.prizeLevel);
  }

  // Signature area
  if (nftData.signature) {
    drawSignature(ctx, nftData.signature);
  }

  // Footer info
  drawFooter(ctx, nftData);

  // For sale badge
  if (nftData.isListed) {
    drawListedBadge(ctx);
  }

  return canvas;
}

/**
 * Draw header
 */
function drawHeader(ctx, nftData) {
  // Title
  ctx.fillStyle = CONFIG.accentColor;
  ctx.font = 'bold 28px "SF Pro Display", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('NFT LOTTERY', CONFIG.width / 2, 60);

  // Subtitle
  ctx.fillStyle = '#666';
  ctx.font = '14px "SF Pro Display", sans-serif';
  ctx.fillText(`TICKET #${nftData.tokenId || '---'}`, CONFIG.width / 2, 85);

  // Separator line
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(40, 100);
  ctx.lineTo(CONFIG.width - 40, 100);
  ctx.stroke();
}

/**
 * Draw number
 */
function drawNumber(ctx, number) {
  const digits = number.toString().padStart(6, '0').split('');

  // Number background
  ctx.fillStyle = '#111';
  ctx.fillRect(30, 140, CONFIG.width - 60, 120);

  // Box for each digit
  const boxWidth = 50;
  const boxHeight = 80;
  const startX = (CONFIG.width - (boxWidth * 6 + 10 * 5)) / 2;
  const startY = 160;

  digits.forEach((digit, index) => {
    const x = startX + index * (boxWidth + 10);

    // Digit background box
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(x, startY, boxWidth, boxHeight);

    // Digit border
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, startY, boxWidth, boxHeight);

    // Digit
    ctx.fillStyle = CONFIG.accentColor;
    ctx.font = 'bold 48px "SF Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(digit, x + boxWidth / 2, startY + boxHeight / 2);
  });

  // LUCKY NUMBER label
  ctx.fillStyle = '#444';
  ctx.font = '12px "SF Pro Display", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('LUCKY NUMBER', CONFIG.width / 2, 275);
}

/**
 * Draw prize badge
 */
function drawPrizeBadge(ctx, level) {
  const labels = ['GRAND PRIZE', 'FIRST PRIZE', 'SECOND PRIZE', 'THIRD PRIZE'];
  const colors = [CONFIG.goldColor, CONFIG.silverColor, CONFIG.bronzeColor, '#888'];

  const label = labels[level] || 'WINNER';
  const color = colors[level] || '#888';

  // Badge background
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(CONFIG.width - 20, 120);
  ctx.lineTo(CONFIG.width - 20, 180);
  ctx.lineTo(CONFIG.width - 100, 180);
  ctx.lineTo(CONFIG.width - 120, 150);
  ctx.lineTo(CONFIG.width - 100, 120);
  ctx.closePath();
  ctx.fill();

  // Badge text
  ctx.fillStyle = '#000';
  ctx.font = 'bold 10px "SF Pro Display", sans-serif';
  ctx.textAlign = 'center';
  ctx.save();
  ctx.translate(CONFIG.width - 60, 150);
  ctx.fillText(label, 0, 0);
  ctx.restore();
}

/**
 * Draw signature area
 */
function drawSignature(ctx, signature) {
  // Signature area background
  ctx.fillStyle = '#0d0d0d';
  ctx.fillRect(30, 300, CONFIG.width - 60, 80);

  // Label
  ctx.fillStyle = '#444';
  ctx.font = '10px "SF Pro Display", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('PERSONAL SIGNATURE', 40, 320);

  // Signature content
  ctx.fillStyle = '#999';
  ctx.font = 'italic 16px "Georgia", serif';
  ctx.textAlign = 'center';

  // Handle long signature
  const maxWidth = CONFIG.width - 80;
  const text = signature.length > 40 ? signature.substring(0, 37) + '...' : signature;
  ctx.fillText(`"${text}"`, CONFIG.width / 2, 355);
}

/**
 * Draw footer info
 */
function drawFooter(ctx, nftData) {
  const y = CONFIG.height - 120;

  // Separator line
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(40, y);
  ctx.lineTo(CONFIG.width - 40, y);
  ctx.stroke();

  // Info grid
  ctx.fillStyle = '#666';
  ctx.font = '11px "SF Pro Display", sans-serif';
  ctx.textAlign = 'left';

  // Draw ID
  ctx.fillText('DRAW PERIOD', 40, y + 30);
  ctx.fillStyle = CONFIG.accentColor;
  ctx.font = 'bold 16px "SF Mono", monospace';
  ctx.fillText(`#${nftData.drawId || '1'}`, 40, y + 50);

  // Dividends
  ctx.fillStyle = '#666';
  ctx.font = '11px "SF Pro Display", sans-serif';
  ctx.fillText('DIVIDENDS', CONFIG.width / 2, y + 30);
  ctx.fillStyle = '#4ade80';
  ctx.font = 'bold 16px "SF Mono", monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`$${nftData.totalDividends || '0.00'}`, CONFIG.width / 2, y + 50);

  // Prizes
  ctx.fillStyle = '#666';
  ctx.font = '11px "SF Pro Display", sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('PRIZES', CONFIG.width - 40, y + 30);
  ctx.fillStyle = CONFIG.goldColor;
  ctx.font = 'bold 16px "SF Mono", monospace';
  ctx.fillText(`$${nftData.totalPrizes || '0.00'}`, CONFIG.width - 40, y + 50);

  // Bottom branding
  ctx.fillStyle = '#333';
  ctx.font = '10px "SF Pro Display", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('NFT LOTTERY - BLOCKCHAIN VERIFIED', CONFIG.width / 2, CONFIG.height - 30);
}

/**
 * Draw for sale badge
 */
function drawListedBadge(ctx) {
  ctx.fillStyle = '#22c55e';
  ctx.beginPath();
  ctx.arc(CONFIG.width - 40, 40, 8, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#22c55e';
  ctx.font = 'bold 10px "SF Pro Display", sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('FOR SALE', CONFIG.width - 55, 44);
}

/**
 * Generate NFT image Data URL
 */
export function generateNFTImage(nftData) {
  const canvas = generateNFTCanvas(nftData);
  return canvas.toDataURL('image/png');
}

/**
 * Download NFT image
 */
export function downloadNFTImage(nftData, filename) {
  const canvas = generateNFTCanvas(nftData);
  const link = document.createElement('a');
  link.download = filename || `NFT-${nftData.number}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

/**
 * Batch generate NFT images
 */
export async function batchGenerateImages(nfts) {
  return nfts.map(nft => ({
    ...nft,
    image: generateNFTImage(nft)
  }));
}

export default {
  generateNFTCanvas,
  generateNFTImage,
  downloadNFTImage,
  batchGenerateImages
};
